import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import alertify from "./../libs/alert"
import EXIF from 'exif-js';
import { useDispatch, useSelector } from 'react-redux';
import { clearImages, setRepeatType } from '../redux/actions/parts';
import { SEGMENT_SETTINGS_UPDATE } from '../redux/reducers/fabric/actions';
import { ACTIONS_CLEAR_ALL } from '../redux/reducers/images';
import './uploadButton.scss'
import { useCopyrightWarning } from '../hooks/useCopyrightWarning';
import { addImagesToPart } from '../redux/actions/parts/addImage';
import { ImageInfo } from '../redux/actions/parts/types/parts';
import { handleFiles } from '../redux/actions/parts/addImage.helpers';
import { setUploadedImgSrc } from '../redux/actions/product';

type ExifData = {
    src: string,
    width: number,
    height: number,
    partWidth: number,
    file: File,
    scaleFactor: number,
    curResolution: number,
    isDefault?: false,//fbdata.isDefault,
    isLibImage?: false
}

export type UploadButtonProps = {
    imageUploadInProgress?: (progress: boolean) => void
    fabricSize: { width: number, height: number }
    onImageSelected: ()=>void,
    partId: string,
    isTileUpload?: boolean
}

export const UploadButton = (props: UploadButtonProps) => {

    const { imageUploadInProgress = () => { }, fabricSize, partId, onImageSelected, isTileUpload } = props
    const { t } = useTranslation();
    const [readyToUploadMode, setreadyToUploadMode] = useState(true)
    const dispatch = useDispatch();
    const minResolution = useSelector((state: any) => state.fbdata?.minimalResolution);
    const selectedSegment = useSelector((state: any) => state.fabric.segmentInFocus);
    const compliant = useSelector((state: any) => state.canvasImages?.isCopyrightCompliant)

    const [selectedFiles, setSelectedFiles] = useState(undefined as File[] | undefined);

    const resolutionSetting = useSelector((state: any) => state.fbdata?.fabricAutoSizeImage);


    const uploaderRef = useRef(undefined as any);

    const pathToImageLibrary = useSelector((state: any) => state.fbdata?.pathToImageLibrary);

    const toggleUploadMode = useCallback(() => {
        setreadyToUploadMode(!readyToUploadMode)
    }, [readyToUploadMode]);

    const uploadHandler = useCallback((compliance: boolean) => (e: any, scaleFactor: number = 1, isDefault: boolean = false, isLibImage: boolean = false) => {
        const validity = compliance;
        if (validity) {
            const files = e.target.files
            if (files.length === 0) {
                setreadyToUploadMode(true)
                e.preventDefault()
                return
            }

            imageUploadInProgress(true)
            handleFiles(files,isLibImage).then((imageExifData) => {
                const images: ImageInfo[] = imageExifData.map((data) => ({
                    ...data, minResolution, fabricSize, repeatType: 'DEFAULT_REPEAT', scaleFactor, isDefault, isLibImage,
                    resolutionSetting,
                    image_id: isLibImage?((data.file as any)?.id||''):''
                }));
                dispatch({ type: ACTIONS_CLEAR_ALL })
                dispatch(clearImages(true))
                dispatch(addImagesToPart(partId, images, scaleFactor || 1, t, true))
                dispatch(setUploadedImgSrc(false))
                
                dispatch(setRepeatType(partId, 'DEFAULT_REPEAT', true))
                dispatch({
                    type: SEGMENT_SETTINGS_UPDATE, payload: {
                        parentCanvas: selectedSegment,
                        repeatType: 'DEFAULT_REPEAT'
                    }
                })

            }).catch((error) => {
                uploaderRef.current && (uploaderRef.current.value = null);
                alertify.alert(t("editor.alert_warning_title1"), t("editor.alert.warning_msg1"), t("i18n.ok"), () => { })
            }).finally(() => {
                imageUploadInProgress(false)
                onImageSelected()
            })
        } else {
            e.target.files?.length && setSelectedFiles(e.target.files)
        }
    }, [imageUploadInProgress, minResolution, fabricSize, partId, selectedSegment, compliant])

    const overrideDefault = useCallback((event: React.DragEvent) => {
        document.getElementById('drop_zone')?.setAttribute('style', "border: 1px solid blue;")
        event.preventDefault()
    }, [])

    const overrideExitDefault = useCallback((event: React.DragEvent) => {
        document.getElementById('drop_zone')?.setAttribute('style', "border: 1px solid white;")
        event.preventDefault()
    }, [])

    const addNodeToDrag = useCallback((file:any)=>{
        if (compliant)
            uploadHandler(true)({ target: { files: [file] } }, 1, false)
        else
            setSelectedFiles([file])
    }, [uploadHandler, compliant])

    const dropHandler = useCallback((event: React.DragEvent) => {
        event.preventDefault()
        const { items, files } = event.dataTransfer
        if (items?.length) {
            const itemCount = items.length
            for (let i = 0; i < itemCount; i++) {
                const dataItem = items[i]
                const isFile = dataItem.type.startsWith("image/")
                const isImage = dataItem.kind === "file"
                if (isFile && isImage) {
                    const file = dataItem.getAsFile()
                    file && addNodeToDrag(file)
                    break;
                }
            }
        } else {
            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const isImage = file.type.startsWith("image/")
                if (isImage) {
                    addNodeToDrag(file)
                    break
                }
            }
        }
    }, [addNodeToDrag]);

    const onRejection = useCallback(() => {
        uploaderRef.current && (uploaderRef.current.value = null);
        setSelectedFiles(undefined)
    }, [])

    const onAcceptance = useCallback((selectedFiles) => {
        uploadHandler(true)({ target: { files: selectedFiles } }, 1, false)
    }, [uploadHandler])

    const copyRightWarning = useCopyrightWarning(compliant, onRejection, onAcceptance, selectedFiles)



    return (
    <div className="dialog imageUploadContainer">
        <div className='dragDropContainer'
                        id="drop_zone"
                        onDrop={dropHandler}
                        onDragOver={overrideDefault}
                        onDragLeave={overrideExitDefault}
                    />
        <div className="dialog upload-dragdrop-label">{t("editor.custom.overlay_upload_your_design_btn_desc")}</div>
        <div className="dialog upload-button">
        {t("editor.custom.overlay_upload_your_design_btn")}
                {isTileUpload ? null:<input
            id="uploadAddImageBtn"
            className="dialog upload-input-concealer"
            type="file"
            accept=".jpg,.jpeg,.png"
                    onChange={readyToUploadMode ? uploadHandler(compliant) : toggleUploadMode}
            onClick={() => { }}
            ref={uploaderRef}
                    multiple={false} />}
        </div>
            {isTileUpload ? <input
                id="uploadAddImageBtn"
                className="dialog upload-input-concealer"
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={readyToUploadMode ? uploadHandler(compliant) : toggleUploadMode}
                onClick={() => { }}
                ref={uploaderRef}
                multiple={false} /> : null}
        {pathToImageLibrary !== undefined ?
            <>
               {!isTileUpload ? <div>
                    <div className="dialog labelOR">OR</div>
                    <div className="dialog pathToImageLibraryBtn"
                        onClick={() => {
                            var action = 'REDIRECT_TO_PATTERNS' ;
                            
                            let url =
                            window.location !== window.parent.location
                                ? document.referrer
                                : document.location.origin
                                window.parent.postMessage(
                                    JSON.stringify({
                                        action,
                                        pathToImageLibrary
                                    }),
                                    url,
                                )                                        
                            //window.parent.location.href = pathToImageLibrary;
                        }}>
                    {t("editor.custom.overlay_path_to_image_library_btn")}
                    </div>
                </div> :null}
            </>
        :null}
        {copyRightWarning}
    </div>
    )
}


